// Small utility to derive a visual color for a variant option value.
// Falls back to neutral gray when we can't map a name.

const COLOR_MAP = {
  // Reds / pinks
  red: "#C0392B", crimson: "#B4183C", ruby: "#9B111E", burgundy: "#6B1E29",
  wine: "#722F37", rose: "#D63A6A", pink: "#EB4A7C", "hot pink": "#FF2B7A",
  fuchsia: "#D33082", magenta: "#C71585", coral: "#F27C6D", peach: "#F3B29B",
  blush: "#E8A6AF", salmon: "#F08972",
  // Nudes / neutrals
  nude: "#E4C0A8", beige: "#D9C0A5", tan: "#C7A17A", caramel: "#B98859",
  bronze: "#8C6239", chocolate: "#5A3418", cocoa: "#4C2A1E",
  mocha: "#7A5A48", latte: "#B79978", almond: "#D5B896",
  toffee: "#8A5B33", cinnamon: "#7C3F1B", terracotta: "#B96A55",
  // Browns
  brown: "#5D3A1F", chestnut: "#6E3B24", espresso: "#3B2416",
  // Metallics
  gold: "#D4B04F", champagne: "#EFD9A6", copper: "#B5734F",
  silver: "#BFBFBF", bronze2: "#8C6239", rose_gold: "#E0A090",
  // Purples / blues / greens
  purple: "#6A4A8F", violet: "#7F5AA5", lilac: "#B296C7",
  navy: "#1F2A44", blue: "#3B5998", teal: "#1A4650",
  sage: "#8C9F82", green: "#587A54", olive: "#6B6B36",
  // Whites / blacks / grays
  white: "#FFFFFF", ivory: "#F3EFE4", cream: "#F5EAD5",
  black: "#0A0A0A", charcoal: "#333333", grey: "#808080", gray: "#808080",
  // Universal
  clear: "#F5F5F5", natural: "#E9DCC7", plum: "#603951", berry: "#7B1E37",
  mauve: "#9E7B87", lavender: "#B7A8C9",
};

// Try to resolve a hex from a free-form option value
export function optionToHex(value) {
  if (!value) return null;
  const v = String(value).toLowerCase().trim();
  // exact match
  if (COLOR_MAP[v]) return COLOR_MAP[v];
  // word-by-word — first hit wins ("rich caramel" -> caramel)
  for (const word of v.split(/[\s\-_/,]+/)) {
    if (COLOR_MAP[word]) return COLOR_MAP[word];
  }
  // hex literal in name? ("#FFAABB")
  const m = v.match(/#[0-9a-f]{6}/i);
  if (m) return m[0];
  return null;
}

// Return an array of swatches [{value, hex, image, variantId, price, available}] for a product,
// pulling from whichever option key looks like a color (color / colour / shade / tone).
export function extractSwatches(product) {
  if (!product?.variants?.length) return [];
  const firstOpts = product.variants[0].options || {};
  const colorKey = Object.keys(firstOpts).find((k) =>
    /^(color|colour|shade|tone|hue)$/i.test(k)
  );
  if (!colorKey) return [];
  const seen = new Set();
  const swatches = [];
  for (const v of product.variants) {
    const val = v.options?.[colorKey];
    if (!val || seen.has(val)) continue;
    seen.add(val);
    swatches.push({
      value: val,
      hex: optionToHex(val),
      image: v.image || null,
      variantId: v.id,
      price: v.price,
      available: v.available !== false,
    });
  }
  return swatches;
}
